import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Box,
  TablePagination,
  Button,
  Grid2,
  Tooltip,
  IconButton,
  Chip,
  Fab,
} from "@mui/material";
import moment from "moment";
import { useGetSkus } from "../../client/hooks/registrations/skus.queries";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import UploadXlmDialog from "./Components/UploadDialgo.component";
import CustomSnackbar from "../components/Message.Component";
import TablePaginationActions from "../components/TablePaginationAction";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import SkuDialog from "./Components/Sku.component";
import TagIcon from "@mui/icons-material/Tag";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PercentIcon from "@mui/icons-material/Percent";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import UpdateIcon from "@mui/icons-material/Update";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

export default function SkuPage() {
  const tableContainerRef = useRef(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState({ page_count: 20, page_index: 0 });
  const [canFetch, setCanFetch] = useState(false);
  const [alert, setAlert] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [openSkuDialog, setOpenSkuDialog] = useState(false);
  const [skuToEdit, setSkuToEdit] = useState(null);

  const [filters, setFilters] = useState(
    JSON.parse(localStorage.getItem("paged_sku_filter")) || {
      dateFrom: "",
      dateTo: "",
    }
  );
  const [openDialog, setOpenDialog] = useState(false);

  // Obtendo os SKUs da API
  const { data, isLoading, isError, refetch, error } = useGetSkus(
    {
      page_index: page.page_index,
      page_count: page.page_count,
      dateFrom:
        filters.dateFrom && filters.dateFrom !== ""
          ? moment(filters.dateFrom, "DD/MM/YYYY").format("YYYY-MM-DD")
          : null,
      dateTo:
        filters.dateTo && filters.dateTo !== ""
          ? moment(filters.dateTo, "DD/MM/YYYY").format("YYYY-MM-DD")
          : null,
    },
    canFetch
  );

  // Atualiza o localStorage ao modificar os filtros
  useEffect(() => {
    localStorage.setItem("paged_sku_filter", JSON.stringify(filters));
  }, [filters]);

  // Define quando deve fazer a requisição
  useEffect(() => {
    setCanFetch(data ? false : true);

    if (isError) {
      const errorMessage =
        error?.response?.data?.msg ||
        error?.message ||
        "Ocorreu um erro inesperado.";

      setErrorMsg(errorMessage);
      setAlert(true);
    }
  }, [data, error, isError]);

  // Função para verificar se um SKU está dentro do intervalo de datas
  const isWithinDateRange = (skuDate) => {
    const createdDate = moment(skuDate);
    const startDate = filters.dateFrom ? moment(filters.dateFrom) : null;
    const endDate = filters.dateTo ? moment(filters.dateTo) : null;

    if (startDate && endDate) {
      return createdDate.isBetween(startDate, endDate, null, "[]");
    } else if (startDate) {
      return createdDate.isSameOrAfter(startDate);
    } else if (endDate) {
      return createdDate.isSameOrBefore(endDate);
    }
    return true;
  };

  // Filtrando os SKUs com base no texto digitado e no intervalo de datas
  const filteredSkus = data?.skus?.filter(
    (sku) =>
      Object.values(sku).some((value) =>
        String(value).toLowerCase().includes(search.toLowerCase())
      ) && isWithinDateRange(sku.createdAt)
  );

  function closeDialog(reload) {
    if (reload) {
      refetch();
    }
    setOpenDialog(false);
  }

  function loadMsg() {
    setOpenDialog(false);
    refetch();
    setAlert(true);
    setSuccessMsg("Arquivos exportados com sucesso");
    setCanFetch(true);
  }

  const handleChangePage = (event, newPage) => {
    setPage({ page_index: newPage, page_count: page.page_count });
    setTimeout(() => {
      refetch();
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
    }, 100);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage({ page_count: parseInt(event.target.value, 10), page_index: 0 });
    setTimeout(() => {
      refetch();
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
    }, 100);
  };

  function handleEditSku(sku) {
    setSkuToEdit(sku);
    setOpenSkuDialog(true);
  }

  function handleCreateSku() {
    setSkuToEdit(null); // novo
    setOpenSkuDialog(true);
  }

  function handleDeleteRequest(sku) {
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <CustomSnackbar
        open={alert}
        onClose={() => setAlert(false)}
        severity={isError ? "error" : "success"}
        message={isError ? errorMsg : successMsg}
      />
      {/* Header da página */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <QrCodeIcon sx={{ fontSize: '2rem', color: '#ff7043', mr: 2 }} />
          <Typography variant="h4" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#1a1a1a',
            fontSize: { xs: '1.75rem', md: '2.125rem' }
          }}>
            Gestão de SKUs
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#666', fontSize: '1rem', ml: 6 }}>
          Controle e monitore os códigos de identificação dos produtos
        </Typography>
      </Box>

      {/* Área de filtros */}
      <Box
        sx={{
          p: 3,
          backgroundColor: "white",
          borderRadius: 3,
          mb: 3,
          border: "1px solid #e8eaed",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <FilterListIcon sx={{ fontSize: '1.5rem', color: '#ff7043', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Filtros e Pesquisa
          </Typography>
        </Box>
        <Grid2 container spacing={3} alignItems="center">
          <Grid2 size={{ xs: 12, md: 5 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Pesquisar SKU ou Código"
              placeholder="Digite para pesquisar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: '#ff7043', mr: 1 }} />,
              }}
              sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#e8eaed",
                  },
                  "&:hover fieldset": {
                    borderColor: "#ff7043",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ff7043",
                    borderWidth: "2px",
                  },
                },
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <Grid2 container spacing={2}>
              <Grid2 size={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data Inicial"
                  size="small"
                  value={filters.dateFrom}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      dateFrom: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ff8b00",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#1e88e5",
                        borderWidth: "2px",
                      },
                    },
                  }}
                />
              </Grid2>
              <Grid2 size={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data Final"
                  size="small"
                  value={filters.dateTo}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, dateTo: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ff8b00",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#1e88e5",
                        borderWidth: "2px",
                      },
                    },
                  }}
                />
              </Grid2>
            </Grid2>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 3 }} display="flex" justifyContent={{ xs: 'center', md: 'flex-end' }}>
            <Button
              onClick={() => setOpenDialog(true)}
              size="large"
              endIcon={<UploadFileIcon />}
              variant="contained"
              sx={{
                height: "42px",
                fontWeight: 600,
                textTransform: "none",
                backgroundColor: "#ff7043",
                color: "white",
                borderRadius: 2,
                px: 3,
                boxShadow: '0 2px 4px rgba(255, 112, 67, 0.2)',
                "&:hover": {
                  backgroundColor: "#c7401a",
                  boxShadow: '0 4px 8px rgba(255, 112, 67, 0.3)',
                },
              }}
            >
              Upload de Arquivos
            </Button>
          </Grid2>
        </Grid2>
      </Box>

      {/* Tabela */}
      <TableContainer
        sx={{ 
          height: "68vh",
          borderRadius: 3,
          border: "1px solid #e8eaed",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        }}
        ref={tableContainerRef}
        component={Paper}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                position: 'sticky',
                top: 0,
                backgroundColor: "#ff7043",
                color: "white",
                fontWeight: 700,
                fontSize: "0.875rem",
                borderBottom: "none",
                zIndex: 100,
                minWidth: 80,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TagIcon fontSize="small" />
                  ID
                </Box>
              </TableCell>
              <TableCell sx={{ 
                position: 'sticky',
                top: 0,
                backgroundColor: "#ff7043",
                color: "white",
                fontWeight: 700,
                fontSize: "0.875rem",
                borderBottom: "none",
                zIndex: 100,
                minWidth: 120,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QrCodeIcon fontSize="small" />
                  Código
                </Box>
              </TableCell>
              <TableCell sx={{ 
                position: 'sticky',
                top: 0,
                backgroundColor: "#ff7043",
                color: "white",
                fontWeight: 700,
                fontSize: "0.875rem",
                borderBottom: "none",
                zIndex: 100,
                minWidth: 130,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon fontSize="small" />
                  Tipo
                </Box>
              </TableCell>
              <TableCell sx={{ 
                position: 'sticky',
                top: 0,
                backgroundColor: "#ff7043",
                color: "white",
                fontWeight: 700,
                fontSize: "0.875rem",
                borderBottom: "none",
                zIndex: 100,
                minWidth: 100,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PercentIcon fontSize="small" />
                  ICMS
                </Box>
              </TableCell>
              <TableCell sx={{ 
                position: 'sticky',
                top: 0,
                backgroundColor: "#ff7043",
                color: "white",
                fontWeight: 700,
                fontSize: "0.875rem",
                borderBottom: "none",
                zIndex: 100,
                minWidth: 130,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayIcon fontSize="small" />
                  Criado
                </Box>
              </TableCell>
              <TableCell sx={{ 
                position: 'sticky',
                top: 0,
                backgroundColor: "#ff7043",
                color: "white",
                fontWeight: 700,
                fontSize: "0.875rem",
                borderBottom: "none",
                zIndex: 100,
                minWidth: 130,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UpdateIcon fontSize="small" />
                  Atualizado
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ 
                position: 'sticky',
                top: 0,
                backgroundColor: "#ff7043",
                color: "white",
                fontWeight: 700,
                fontSize: "0.875rem",
                borderBottom: "none",
                zIndex: 100,
                minWidth: 120,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <SettingsIcon fontSize="small" />
                  Ações
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="error">
                    Erro ao carregar os SKUs.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredSkus?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhum SKU encontrado.
                </TableCell>
              </TableRow>
            ) : (
              filteredSkus?.map((sku, index) => (
                <TableRow 
                  key={sku.id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                    borderLeft: `3px solid ${sku.imported ? '#4caf50' : '#ff9800'}`,
                    "&:hover": {
                      backgroundColor: "#fff3e0",
                      boxShadow: "inset 0 0 0 1px #ff7043",
                    },
                    "& .MuiTableCell-root": {
                      borderBottom: "1px solid #e8eaed",
                      fontSize: "0.875rem",
                      py: 2,
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, color: '#ff7043' }}>
                    #{sku.id}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.9rem' }}>
                    {sku.code}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={sku.imported ? <CheckCircleIcon sx={{ fontSize: '16px !important' }} /> : <UploadFileIcon sx={{ fontSize: '16px !important' }} />}
                      label={sku.imported ? "Importado" : "Nacional"}
                      variant="filled"
                      size="small"
                      sx={{
                        borderRadius: "20px",
                        fontWeight: 600,
                        fontSize: "11px",
                        height: "28px",
                        backgroundColor: sku.imported ? "#e8f5e8" : "#fff3e0",
                        color: sku.imported ? "#2e7d32" : "#ef6c00",
                        border: sku.imported ? "1px solid #c8e6c9" : "1px solid #ffcc02",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        "&:hover": {
                          backgroundColor: sku.imported ? "#c8e6c9" : "#ffe0b2",
                          transform: "scale(1.05)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<PercentIcon sx={{ fontSize: '16px !important' }} />}
                      label={`${(sku.icms * 100).toFixed(1)}%`}
                      variant="filled"
                      size="small"
                      sx={{
                        borderRadius: "20px",
                        fontWeight: 600,
                        fontSize: "11px",
                        height: "28px",
                        backgroundColor: "#fff3e0",
                        color: "#c7401a",
                        border: "1px solid #ffcc80",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                        "&:hover": {
                          backgroundColor: "#ffcc80",
                          transform: "scale(1.05)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#666', fontWeight: 500 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarTodayIcon sx={{ fontSize: '14px', color: '#ff7043' }} />
                      {new Date(sku.createdAt).toLocaleDateString('pt-BR')}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#666', fontWeight: 500 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UpdateIcon sx={{ fontSize: '14px', color: '#ff7043' }} />
                      {new Date(sku.updatedAt).toLocaleDateString('pt-BR')}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{ display: "flex", justifyContent: "center", gap: 1 }}
                    >
                      <Tooltip title="Editar SKU">
                        <IconButton
                          onClick={() => handleEditSku(sku)}
                          size="small"
                          sx={{
                            bgcolor: "#fff3e0",
                            color: "#ff7043",
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "#ff7043",
                              color: "white",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <CreateOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover SKU">
                        <IconButton
                          onClick={() => handleDeleteRequest(sku.id)}
                          size="small"
                          sx={{
                            bgcolor: "#ffebee",
                            color: "#d32f2f",
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "#d32f2f",
                              color: "white",
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Fab
        color="primary"
        aria-label="Adicionar novo SKU"
        onClick={() => handleCreateSku()}
        sx={{
          position: "fixed",
          bottom: 32,
          right: 32,
          width: 64,
          height: 64,
          boxShadow: "0 6px 16px rgba(255, 112, 67, 0.3)",
          backgroundColor: "#ff7043",
          color: "white",
          border: "none",
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor: "#c7401a",
            boxShadow: "0 8px 20px rgba(255, 112, 67, 0.4)",
            transform: "scale(1.05)",
          },
          "&:active": {
            transform: "scale(0.95)",
          },
        }}
      >
        <AddIcon fontSize="large" />
      </Fab>
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderTop: "1px solid #e8eaed",
          borderRadius: "0 0 12px 12px",
          boxShadow: "0px -2px 8px rgba(0,0,0,0.08)",
          display: "flex",
          justifyContent: "flex-end",
          p: 1,
        }}
      >
        <TablePagination
          rowsPerPageOptions={[20, 50, 100]}
          colSpan={3}
          count={data?.total || 0}
          rowsPerPage={page.page_count}
          page={page.page_index}
          labelRowsPerPage="Itens por pagina"
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          disabled={isLoading}
        />
      </Box>
      <UploadXlmDialog
        open={openDialog}
        onClose={closeDialog}
        onSave={loadMsg}
      />
      <SkuDialog
        open={openSkuDialog}
        onClose={() => setOpenSkuDialog(false)}
        onSave={() => {
          refetch();
          setOpenSkuDialog(false);
        }}
        initialData={skuToEdit}
      />
    </Box>
  );
}
